import React from 'react';
import {
    type Layout,
    LAYOUT_FORMS_FULL,
    useAdditionnalCollectionRequest,
    useDossierFormulaireContext,
    useDossierRoutingContext,
    useSelectedDossierContext,
    useTransition,
} from '@europrocurement/l2d-modules';
import { Box, CircularProgress, useTheme } from '@mui/material';
import { useNavigate } from 'react-router';
import { ConfirmationModal, useModal } from '@europrocurement/flexy-components';
import { ApiDossiersIdActionPutActionEnum } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useSnackbar } from 'notistack';
import DossierFormValidationStatusModal from '@europrocurement/l2d-modules/modules/Dossiers/components/detail/DossierFormValidationStatusModal/DossierFormValidationStatusModal';
import _ from 'lodash';
import DossierFormsBody from '../DossierFormsBody/DossierFormsBody';
import DossierValidationActions from '../DossierValidationActions/DossierValidationActions';
import { useDossierFormValidation, useDossierProcesses } from '../../hooks';
import DossierFormsValidationModal from '../DossierFormsValidationModal/DossierFormsValidationModal';

export type DossierFormsValidationProps = {
    currentLayout: Layout;
};

const DossierFormsValidation: React.FunctionComponent<DossierFormsValidationProps> = function ({
    currentLayout,
}) {
    const {
        dataLoaded,
        getCurrentFormulaire,
        persistAllFormulaires,
        updateFormulaire,
        hasNonPersistedChanges,
    } = useDossierFormulaireContext();
    const { dossier } = useSelectedDossierContext();
    const { cancel } = useDossierFormValidation();
    const { canApplyTransition, applyTransition } = useTransition();
    const { getDossierRoute } = useDossierRoutingContext();
    const navigate = useNavigate();
    const { modalActions } = useModal();
    const { enqueueSnackbar } = useSnackbar();
    const { canPublish, publishGreffeValidatorProcess } = useDossierProcesses();
    const { callAdditionnalCollectionRequestModal } = useAdditionnalCollectionRequest();
    const { palette } = useTheme();

    if (!dataLoaded) {
        return (
            <Box
                data-testid="form-validation-loading-icon"
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100%',
                    padding: '1em 0',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    const formulaire = getCurrentFormulaire();

    if (!formulaire) {
        return null;
    }

    const handleChange = (data: Record<string, unknown>) => {
        updateFormulaire({
            ...formulaire,
            prestationJson: _.merge({}, formulaire.prestationJson, data),
        });
    };

    const handleCancel = () => {
        cancel(formulaire.dossierId);
    };

    const handleSendMessage = () => {
        if (hasNonPersistedChanges) {
            modalActions.call(
                <ConfirmationModal
                    messages={{
                        question: 'Enregistrer les modifications ?',
                        detail: {
                            message:
                                'Vous avez des modifications en cours sur le formulaire. Souhaitez-vous sauvegarder vos modifications?',
                            severity: 'warning' as const,
                        },
                        validation: 'Sauvegarder',
                        annulation: 'Continuer',
                    }}
                    color={palette.primary}
                    actionOnValidation={async () => {
                        await persistAllFormulaires();
                        callAdditionnalCollectionRequestModal();
                    }}
                    actionOnCancellation={() => {
                        callAdditionnalCollectionRequestModal();
                    }}
                />,
            );
        } else {
            callAdditionnalCollectionRequestModal();
        }
    };

    const handleSubmitAndPublish = async () => {
        try {
            await persistAllFormulaires();
            await publishGreffeValidatorProcess(dossier);

            navigate(getDossierRoute(dossier.id as unknown as number));
            enqueueSnackbar('Votre dossier a bien été sauvegardé et déposé au greffe');
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Une erreur est survenue lors de la sauvegarde du dossier', {
                variant: 'error',
            });
        } finally {
            modalActions.close();
        }
    };

    const handleSubmit = async (actionIdentifier: ApiDossiersIdActionPutActionEnum | 'save') => {
        try {
            await persistAllFormulaires();
            if (actionIdentifier !== 'save') {
                if (canApplyTransition(dossier, actionIdentifier)) {
                    await applyTransition(dossier, actionIdentifier);
                } else {
                    enqueueSnackbar('Statut non disponible pour ce dossier', {
                        variant: 'error',
                    });
                }
            }
            navigate(getDossierRoute(dossier.id as unknown as number));
            enqueueSnackbar('Votre dossier a bien été sauvegardé');
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Une erreur est survenue lors de la sauvegarde du dossier', {
                variant: 'error',
            });
        } finally {
            modalActions.close();
        }
    };

    const handleSave = async () => {
        if (canPublish(dossier)) {
            modalActions.call(
                <DossierFormsValidationModal
                    onSaveAndPublish={() => handleSubmitAndPublish()}
                    onSaveWithoutPublishing={() => handleSubmit('save')}
                    onCancel={() => modalActions.close()}
                />,
            );
        } else {
            modalActions.call(
                <DossierFormValidationStatusModal
                    dossier={dossier}
                    withoutSaveForm={false}
                    handleSubmit={handleSubmit}
                />,
            );
        }
    };

    const fullLayoutSx =
        currentLayout === LAYOUT_FORMS_FULL ? { maxWidth: '40rem', margin: 'auto' } : {};

    return (
        <Box
            sx={{
                height: '80vh',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: palette.background.paper,
                borderRadius: '12px',
                ...fullLayoutSx,
            }}
        >
            <DossierFormsBody
                formulaire={formulaire}
                onChange={handleChange}
            />
            <DossierValidationActions
                onCancel={handleCancel}
                onSendMessage={handleSendMessage}
                onValidate={handleSave}
            />
        </Box>
    );
};

export default DossierFormsValidation;
